import type { TimeEntryItem, TimeReportData, TimeReportGroup } from '@hygge/shared';
import type { SpreadsheetColumn } from '@/lib/spreadsheet';
import { viewerTimeZone } from '@/lib/timezone';
import { entryStartWall, formatEntryDate } from './wall-clock';

/**
 * The report table as printed into the PDF, built from the report DATA.
 *
 * It used to be built by cloning the on-screen table (`reportRef.cloneNode`),
 * which meant the PDF contained exactly what the DOM contained. That is fine
 * while the table renders every row, and silently wrong the moment it does not:
 * with pagination the export would carry one page of rows under a footer and
 * summary cards that still describe the whole report — an internally
 * inconsistent document sent to a client, with nothing on screen to reveal it.
 *
 * Building from data makes truncation impossible by construction: pagination
 * state is not an input here, so no change to what is displayed can shrink the
 * export.
 *
 * The print window loads only the inline <style> in `handleSavePdf` — no
 * Tailwind — so this markup is deliberately class-free apart from
 * `group-header`, which that stylesheet already targets.
 */

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

export function formatHours(seconds: number): string {
  return (seconds / 3600).toFixed(2);
}

/**
 * Entry dates print as "Jul 30" — no year, matching the on-screen table — and
 * name the author's wall day, the UTC digits `startedAt` holds, so the PDF
 * files a row under the same day for whoever exports it. [HW-916] A legacy
 * row the backfill has not converted yet names its instant's day in the
 * exporter's zone instead — the day it printed before HW-916 and still prints
 * on screen. `viewerZone` is resolved once per document by the two builders
 * below and handed to every row. [HW-916]
 */
function entryRowDate(entry: TimeEntryItem, viewerZone: string): string {
  return formatEntryDate(entryStartWall(entry, viewerZone), { month: 'short', day: 'numeric' });
}

/**
 * React escaped every value on its way into the DOM; concatenating a string
 * does not. This output is handed to `document.write`, so a description holding
 * `<`, `&` or `</td>` would corrupt — or inject into — a client-facing document.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface BuildReportTableOptions {
  /**
   * Drop Logged Hours from the document entirely — the column's header, cells
   * and footer, and the figure in each group banner. A report exported with
   * this on must not carry a recorded total anywhere: the switch behind it
   * exists so a client sees Billable Hours only.
   */
  hideLogged: boolean;
  /** Company-wide name format; supplied by the caller since it comes from a hook. */
  formatName: (employee: { firstName: string; lastName: string }) => string;
}

/**
 * Billable Hours of one entry — the value the row STORES. Until HW-973 this was
 * a typed override held in the Reports tab's memory, lost on reload and
 * invisible to a colleague; now it is a column, changed through the API by
 * "Edit any time log" (and on screen by HW-974).
 *
 * The three totals below are the only way anything should add these up. They
 * live here, next to the formatters, for the same reason the formatters do: the
 * on-screen footer, the group banners and the PDF must never be able to disagree
 * about a number that goes to a client.
 *
 * None of them takes pagination as an input — a total is over the whole report,
 * never over the page being displayed.
 */
export function billableSeconds(
  entry: Pick<TimeEntryItem, 'billableDuration' | 'duration'>
): number {
  // The report read always carries the figure. An entry from a read that
  // leaves it out for this caller (decision 2026-10-08) bills its Logged
  // Hours here rather than printing NaN — the figure a new entry starts at.
  return entry.billableDuration ?? entry.duration;
}

/**
 * Whether a holder of "Edit any time log" set this entry's Billable Hours
 * apart — read off the stored adjustment marker, not off the figures. A
 * holder's billable that differs from the duration marks the entry (who and
 * when are stored); one equal to the duration clears the mark. So an entry
 * stays adjusted after its owner changes the duration, even onto the billable
 * figure itself, and only a holder can withdraw it (product decision,
 * 2026-10-08). [HW-973]
 */
export function isAdjusted(entry: Pick<TimeEntryItem, 'billableAdjustedAt'>): boolean {
  // `!= null`: a payload without the field is unadjusted, not marked.
  return entry.billableAdjustedAt != null;
}

/** Billable seconds across one group's full entry list. */
export function groupBillableSeconds(group: TimeReportGroup): number {
  let total = 0;
  for (const entry of group.entries) total += billableSeconds(entry);
  return total;
}

/** Billable seconds across every entry of every group in the report. */
export function reportBillableSeconds(report: TimeReportData): number {
  let total = 0;
  for (const group of report.groups) total += groupBillableSeconds(group);
  return total;
}

function groupBannerHtml(
  group: TimeReportGroup,
  report: TimeReportData,
  opts: BuildReportTableOptions
): string {
  // Derived here rather than taken as a parameter: the span and the figure both
  // follow from `hideLogged`, and passing the count alongside the flag let a
  // caller hand over a colspan that contradicted the columns.
  const columnCount = opts.hideLogged ? 5 : 6;
  const label = (report.groupBy === 'project' ? group.projectName : group.employeeName) ?? '';

  const groupBillable = groupBillableSeconds(group);

  // With logged hours hidden the banner carries the billable total instead of
  // the recorded one, and drops the comparison: the two figures side by side
  // are exactly what the switch was asked to keep out of the document, and a
  // lone "(billable: …)" next to nothing to compare it against says nothing.
  if (opts.hideLogged) {
    return `<tr class="group-header"><td colspan="${columnCount}">${escapeHtml(label)}<span>— ${formatDuration(groupBillable)}</span></td></tr>`;
  }

  // Only annotate when the group bills something other than it logs.
  //
  // The word is "billable", matching the column this number is under — the
  // on-screen name since HW-973. The yes/no flag `isBillable` ("does this hour
  // go on a bill at all") is a different thing and never prints here.
  const billableNote =
    groupBillable !== group.totalSeconds
      ? `<span>(billable: ${formatDuration(groupBillable)})</span>`
      : '';

  // The colour dot on screen is a zero-content span sized by Tailwind, so it
  // renders as nothing in the print window — omitted rather than faked.
  return `<tr class="group-header"><td colspan="${columnCount}">${escapeHtml(label)}<span>— ${formatDuration(group.totalSeconds)}${billableNote}</span></td></tr>`;
}

function entryRowHtml(
  entry: TimeEntryItem,
  report: TimeReportData,
  opts: BuildReportTableOptions,
  viewerZone: string
): string {
  const cells = [
    `<td>${escapeHtml(entryRowDate(entry, viewerZone))}</td>`,
    report.groupBy === 'project'
      ? `<td>${escapeHtml(opts.formatName(entry.employee))}</td>`
      : `<td>${escapeHtml(entry.project.name)}</td>`,
    `<td>${escapeHtml(entry.task?.key ?? '—')}</td>`,
    `<td>${escapeHtml(entry.description || '—')}</td>`,
    ...(opts.hideLogged ? [] : [`<td>${formatHours(entry.duration)}</td>`]),
    `<td>${formatHours(billableSeconds(entry))}</td>`,
  ];
  return `<tr>${cells.join('')}</tr>`;
}

/**
 * The complete report table, every entry included regardless of what the screen
 * is currently paging to.
 *
 * Billable Hours must stay the last column and Logged Hours second-to-last: the
 * print stylesheet right-aligns them positionally (`:last-child`,
 * `:nth-last-child(2)`), not by class.
 */
export function buildReportTableHtml(
  report: TimeReportData,
  opts: BuildReportTableOptions
): string {
  const headCells = [
    '<th>Date</th>',
    report.groupBy === 'project' ? '<th>Employee</th>' : '<th>Project</th>',
    '<th>Task</th>',
    '<th>Description</th>',
    ...(opts.hideLogged ? [] : ['<th>Logged Hours</th>']),
    '<th>Billable Hours</th>',
  ].join('');

  const bodyRows: string[] = [];
  const viewerZone = viewerTimeZone();

  for (const group of report.groups) {
    bodyRows.push(groupBannerHtml(group, report, opts));
    for (const entry of group.entries) {
      bodyRows.push(entryRowHtml(entry, report, opts, viewerZone));
    }
  }

  const billableTotal = reportBillableSeconds(report);

  // "Total" spans Date + Project|Employee + Task + Description in both modes.
  const footCells = [
    '<td colspan="4">Total</td>',
    ...(opts.hideLogged ? [] : [`<td>${formatHours(report.totalSeconds)}</td>`]),
    `<td>${formatHours(billableTotal)}</td>`,
  ].join('');

  return (
    `<table>` +
    `<thead><tr>${headCells}</tr></thead>` +
    `<tbody>${bodyRows.join('')}</tbody>` +
    `<tfoot><tr>${footCells}</tr></tfoot>` +
    `</table>`
  );
}

/** One line of the report as a spreadsheet row. */
export interface ReportSheetRow {
  /** The project or the person the group is about, repeated on every one of its rows. */
  group: string;
  date: string;
  /** Employee when grouped by project, project when grouped by employee. */
  counterpart: string;
  task: string;
  description: string;
  /** Logged Hours, not seconds, so a spreadsheet can sum them. Null when the column is hidden. */
  loggedHours: number | null;
  /** Billable Hours, the figure a client is billed. */
  billableHours: number;
  /** The closing Total line, which carries no entry of its own. */
  isTotal?: boolean;
}

/** Hours as a number, rounded exactly the way the printed table rounds them. */
function hoursOf(seconds: number): number {
  return Number(formatHours(seconds));
}

/**
 * The report as spreadsheet rows: one per entry, each carrying its group, and a
 * closing Total.
 *
 * Built from the same data and the same totals as the printed table on purpose —
 * accounting asked for the Excel export precisely so they could add these
 * numbers up themselves, so the sheet and the PDF must not be able to disagree.
 * A group banner has no equivalent in a sheet, so the group becomes a column
 * instead; every other column matches the table, in the same order.
 */
export function buildReportRows(
  report: TimeReportData,
  opts: BuildReportTableOptions
): ReportSheetRow[] {
  const rows: ReportSheetRow[] = [];
  const viewerZone = viewerTimeZone();

  for (const group of report.groups) {
    const groupLabel =
      (report.groupBy === 'project' ? group.projectName : group.employeeName) ?? '';
    for (const entry of group.entries) {
      rows.push({
        group: groupLabel,
        date: entryRowDate(entry, viewerZone),
        counterpart:
          report.groupBy === 'project' ? opts.formatName(entry.employee) : entry.project.name,
        task: entry.task?.key ?? '',
        description: entry.description || '',
        loggedHours: opts.hideLogged ? null : hoursOf(entry.duration),
        billableHours: hoursOf(billableSeconds(entry)),
      });
    }
  }

  rows.push({
    group: '',
    date: '',
    counterpart: '',
    task: '',
    description: 'Total',
    loggedHours: opts.hideLogged ? null : hoursOf(report.totalSeconds),
    billableHours: hoursOf(reportBillableSeconds(report)),
    isTotal: true,
  });

  return rows;
}

/**
 * The spreadsheet's columns for `buildReportRows`' rows — the Excel and CSV
 * files of Reports → Show entries. Same order as the printed table, the group
 * as its own first column, and the Logged Hours column dropped exactly when
 * `hideLogged` drops the figure from the rows: a column of empty cells under
 * "Logged Hours" would still tell the client the column exists. [HW-973]
 */
export function reportSheetColumns(
  groupBy: TimeReportData['groupBy'],
  hideLogged: boolean
): SpreadsheetColumn<ReportSheetRow>[] {
  return [
    { header: groupBy === 'project' ? 'Project' : 'Employee', width: 26, value: (r) => r.group },
    { header: 'Date', width: 12, value: (r) => r.date },
    {
      header: groupBy === 'project' ? 'Employee' : 'Project',
      width: 26,
      value: (r) => r.counterpart,
    },
    { header: 'Task', width: 14, value: (r) => r.task },
    { header: 'Description', width: 48, value: (r) => r.description },
    ...(hideLogged
      ? []
      : [{ header: 'Logged Hours', width: 14, value: (r: ReportSheetRow) => r.loggedHours }]),
    { header: 'Billable Hours', width: 16, value: (r) => r.billableHours },
  ];
}
